import React, { useEffect, useState } from "react";
import { Row, Col, Card, CardBody, CardTitle, Form, FormGroup, Label, Input, Button, Alert, Spinner, ListGroup, ListGroupItem } from "reactstrap";
import Breadcrumbs from "../../../components/Common/Breadcrumb";
import { apiJson } from "../../../helpers/apiConfig";

/**
 * Hizmet Talebi Gir — adminin Hizmet'eGEL sağlayıcılarına talep açması.
 *
 * api/Hizmet/TalepOlustur kullanılır; o sektördeki (yarıçap içindeki) sağlayıcılara
 * bildirim gider. "Kimin adına" seçilirse talep o kullanıcıya ait olur ve teklifler
 * ona düşer (telefonla ulaşan müşteri vb.); seçilmezse talep admin hesabına açılır.
 */

const BOS = { sektor: "", baslik: "", aciklama: "", konum: "", butce: "", tarih: "", yaricap: 100 };

const HizmetTalebiGir = () => {
  document.title = "Hizmet Talebi Gir | HarmanaGel Admin";

  const [sektorler, setSektorler] = useState([]);
  const [form, setForm] = useState(BOS);
  const [kullaniciAra, setKullaniciAra] = useState("");
  const [kullaniciSonuc, setKullaniciSonuc] = useState([]);
  const [adina, setAdina] = useState(null); // { ID, AD_SOYAD, CEP_NO }
  const [gonderiliyor, setGonderiliyor] = useState(false);
  const [hata, setHata] = useState(null);
  const [basari, setBasari] = useState(null);

  useEffect(() => {
    apiJson("/HIZMET_SEKTORLERI?$orderby=AD asc")
      .then((j) => setSektorler(j?.value || []))
      .catch(() => setHata("Hizmet sektörleri alınamadı."));
  }, []);

  // Adına talep açılacak kullanıcı araması (ad / telefon / ID).
  useEffect(() => {
    const q = kullaniciAra.trim();
    if (q.length < 2) { setKullaniciSonuc([]); return undefined; }
    const t = setTimeout(async () => {
      try {
        const k = q.replace(/'/g, "''");
        const filtre = /^\d+$/.test(q)
          ? `ID eq ${q} or contains(CEP_NO,'${k}')`
          : `contains(AD_SOYAD,'${k}')`;
        const j = await apiJson(`/KULLANICILAR?$filter=${encodeURIComponent(filtre)}&$select=ID,AD_SOYAD,CEP_NO&$top=10`);
        setKullaniciSonuc(j?.value || []);
      } catch {
        setKullaniciSonuc([]);
      }
    }, 350);
    return () => clearTimeout(t);
  }, [kullaniciAra]);

  const alan = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const gonder = async (e) => {
    e.preventDefault();
    setHata(null);
    setBasari(null);
    if (!form.sektor) return setHata("Hizmet sektörü seçin.");
    if (!form.baslik.trim()) return setHata("Başlık zorunlu.");
    setGonderiliyor(true);
    try {
      const sonuc = await apiJson("/api/Hizmet/TalepOlustur", {
        method: "POST",
        body: JSON.stringify({
          HizmetSektoruId: Number(form.sektor),
          Baslik: form.baslik.trim(),
          Aciklama: form.aciklama.trim() || null,
          KonumMetni: form.konum.trim() || null,
          Butce: form.butce ? Number(String(form.butce).replace(",", ".")) : null,
          IstenenTarih: form.tarih ? new Date(form.tarih).toISOString() : null,
          BildirimYaricapKm: Number(form.yaricap) || 100,
          AdinaKullaniciId: adina?.ID || null,
        }),
      });
      setBasari(
        `Talep #${sonuc?.TalepId} yayınlandı — ${sonuc?.BilgilendirilenSaglayici ?? 0} sağlayıcıya bildirim gitti` +
        (adina ? ` (${adina.AD_SOYAD} adına).` : ".")
      );
      setForm(BOS);
      setAdina(null);
      setKullaniciAra("");
    } catch (err) {
      setHata(err.status === 401 ? "Başka kullanıcı adına talep açma yalnız adminlere açık." : `Talep açılamadı: ${err.message}`);
    } finally {
      setGonderiliyor(false);
    }
  };

  return (
    <React.Fragment>
      <div className="page-content">
        <div className="container-fluid">
          <Breadcrumbs title="Hizmet'eGEL" breadcrumbItem="Hizmet Talebi Gir" />
          {hata ? <Alert color="danger" toggle={() => setHata(null)}>{hata}</Alert> : null}
          {basari ? <Alert color="success" toggle={() => setBasari(null)}>{basari}</Alert> : null}

          <Row>
            <Col xl={8}>
              <Card>
                <CardBody>
                  <CardTitle className="mb-3">İş sağlayıcılara talep aç</CardTitle>
                  <Form onSubmit={gonder}>
                    <Row>
                      <Col md={6}>
                        <FormGroup>
                          <Label>Hizmet sektörü *</Label>
                          <Input type="select" value={form.sektor} onChange={alan("sektor")}>
                            <option value="">Seçin…</option>
                            {sektorler.map((s) => <option key={s.ID} value={s.ID}>{s.AD}</option>)}
                          </Input>
                        </FormGroup>
                      </Col>
                      <Col md={6}>
                        <FormGroup>
                          <Label>Konum (il / ilçe)</Label>
                          <Input value={form.konum} onChange={alan("konum")} placeholder="Örn: Konya, Ereğli" />
                        </FormGroup>
                      </Col>
                    </Row>
                    <FormGroup>
                      <Label>Başlık *</Label>
                      <Input value={form.baslik} onChange={alan("baslik")} maxLength={200} placeholder="Örn: 40 dönüm buğday hasadı için biçerdöver" />
                    </FormGroup>
                    <FormGroup>
                      <Label>Açıklama</Label>
                      <Input type="textarea" rows={4} value={form.aciklama} onChange={alan("aciklama")} />
                    </FormGroup>
                    <Row>
                      <Col md={4}>
                        <FormGroup>
                          <Label>Bütçe (₺)</Label>
                          <Input value={form.butce} onChange={alan("butce")} inputMode="decimal" placeholder="Boş bırakılabilir" />
                        </FormGroup>
                      </Col>
                      <Col md={4}>
                        <FormGroup>
                          <Label>İstenen tarih</Label>
                          <Input type="date" value={form.tarih} onChange={alan("tarih")} />
                        </FormGroup>
                      </Col>
                      <Col md={4}>
                        <FormGroup>
                          <Label>Bildirim yarıçapı (km)</Label>
                          <Input type="number" min={5} max={1000} value={form.yaricap} onChange={alan("yaricap")} />
                        </FormGroup>
                      </Col>
                    </Row>
                    <Button color="primary" type="submit" disabled={gonderiliyor}>
                      {gonderiliyor ? <Spinner size="sm" /> : <><i className="bx bx-send me-1" />Talebi Yayınla</>}
                    </Button>
                  </Form>
                </CardBody>
              </Card>
            </Col>
            <Col xl={4}>
              <Card>
                <CardBody>
                  <CardTitle className="mb-2">Kimin adına? (opsiyonel)</CardTitle>
                  <p className="text-muted small">
                    Seçilirse talep bu kullanıcıya ait olur; teklifler ve bildirimler ona gider.
                    Seçilmezse talep sizin hesabınıza açılır.
                  </p>
                  {adina ? (
                    <Alert color="info" className="d-flex justify-content-between align-items-center mb-0">
                      <span>#{adina.ID} {adina.AD_SOYAD} {adina.CEP_NO ? `· ${adina.CEP_NO}` : ""}</span>
                      <Button size="sm" color="link" onClick={() => setAdina(null)}>Kaldır</Button>
                    </Alert>
                  ) : (
                    <>
                      <Input value={kullaniciAra} onChange={(e) => setKullaniciAra(e.target.value)} placeholder="Ad soyad, telefon veya ID" />
                      {kullaniciSonuc.length > 0 && (
                        <ListGroup className="mt-2">
                          {kullaniciSonuc.map((k) => (
                            <ListGroupItem key={k.ID} action tag="button" type="button" onClick={() => { setAdina(k); setKullaniciSonuc([]); }}>
                              #{k.ID} {k.AD_SOYAD} <span className="text-muted small">{k.CEP_NO}</span>
                            </ListGroupItem>
                          ))}
                        </ListGroup>
                      )}
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    </React.Fragment>
  );
};

export default HizmetTalebiGir;
